import type { TransitionKind } from "../scene/schema";

/**
 * Transitions between main track clips. The export uses ffmpeg's `xfade`
 * filter; the editor preview draws the same effects into its canvas with the
 * formulas of that filter (see `transitionFrame`), so both look alike.
 */

export const TRANSITION_LABELS: Record<TransitionKind, string> = {
  crossfade: "Überblenden",
  "fade-black": "Über Schwarz",
  "fade-white": "Über Weiß",
  "slide-left": "Schieben nach links",
  "slide-right": "Schieben nach rechts",
  "slide-up": "Schieben nach oben",
  "slide-down": "Schieben nach unten",
  "wipe-left": "Wischen nach links",
  "wipe-right": "Wischen nach rechts",
  zoom: "Zoom",
  circle: "Kreis",
};

/** Name of the matching `xfade` transition (available since ffmpeg 5.0). */
export const XFADE_TRANSITIONS: Record<TransitionKind, string> = {
  crossfade: "fade",
  "fade-black": "fadeblack",
  "fade-white": "fadewhite",
  "slide-left": "slideleft",
  "slide-right": "slideright",
  "slide-up": "slideup",
  "slide-down": "slidedown",
  "wipe-left": "wipeleft",
  "wipe-right": "wiperight",
  zoom: "zoomin",
  circle: "circleopen",
};

export const DEFAULT_TRANSITION_MS = 500;

/** How one of the two clips is drawn at a moment of the transition. */
export interface TransitionLayer {
  /** Weight (additive mixes) or opacity. */
  opacity: number;
  /** Offset in fractions of the frame size. */
  dx: number;
  dy: number;
  /** Scale around the frame centre. */
  scale: number;
  /** Visible part in fractions of the frame (wipes). */
  clip?: { x: number; y: number; width: number; height: number };
}

export interface TransitionFrame {
  from: TransitionLayer;
  to: TransitionLayer;
  /**
   * `mix`: the weights of both pictures (plus black/white) add up to 1, drawn
   * additively. `stack`: the incoming picture lies on top of the outgoing one.
   * `radial`: the incoming picture shows inside a soft circle.
   */
  mode: "mix" | "stack" | "radial";
  black: number;
  white: number;
  /** `radial`: fully visible up to `inner`, hidden beyond `outer` (fractions of the half diagonal). */
  inner?: number;
  outer?: number;
}

const smoothstep = (edge0: number, edge1: number, x: number) => {
  const t = Math.max(0, Math.min(1, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
};

const layer = (input: Partial<TransitionLayer> = {}): TransitionLayer => ({
  opacity: 1,
  dx: 0,
  dy: 0,
  scale: 1,
  ...input,
});

/**
 * What the picture looks like at `progress` (0 = only the outgoing clip,
 * 1 = only the incoming one). xfade counts its own progress from 1 down to 0
 * (`q` below); the formulas follow libavfilter/vf_xfade.c.
 */
export function transitionFrame(kind: TransitionKind, progress: number): TransitionFrame {
  const p = Math.max(0, Math.min(1, progress));
  const q = 1 - p;
  const plain = { black: 0, white: 0 };
  switch (kind) {
    case "crossfade":
      return {
        mode: "mix",
        from: layer({ opacity: q }),
        to: layer({ opacity: p }),
        ...plain,
      };
    case "fade-black":
    case "fade-white": {
      // mix(mix(a, bg, s1), mix(bg, b, s2), q) with mix(x, y, m) = x·m + y·(1 − m)
      const s1 = smoothstep(0.8, 1, q);
      const s2 = smoothstep(0.2, 1, q);
      const from = q * s1;
      const to = p * (1 - s2);
      const background = Math.max(0, 1 - from - to);
      return {
        mode: "mix",
        from: layer({ opacity: from }),
        to: layer({ opacity: to }),
        black: kind === "fade-black" ? background : 0,
        white: kind === "fade-white" ? background : 0,
      };
    }
    case "slide-left":
      return { mode: "stack", from: layer({ dx: -p }), to: layer({ dx: q }), ...plain };
    case "slide-right":
      return { mode: "stack", from: layer({ dx: p }), to: layer({ dx: -q }), ...plain };
    case "slide-up":
      return { mode: "stack", from: layer({ dy: -p }), to: layer({ dy: q }), ...plain };
    case "slide-down":
      return { mode: "stack", from: layer({ dy: p }), to: layer({ dy: -q }), ...plain };
    case "wipe-left":
      return {
        mode: "stack",
        from: layer(),
        to: layer({ clip: { x: q, y: 0, width: p, height: 1 } }),
        ...plain,
      };
    case "wipe-right":
      return {
        mode: "stack",
        from: layer(),
        to: layer({ clip: { x: 0, y: 0, width: p, height: 1 } }),
        ...plain,
      };
    case "zoom": {
      // The outgoing picture zooms in (to a single colour), then fades over.
      const zoom = smoothstep(0.5, 1, q);
      const fade = smoothstep(0, 0.5, q);
      return {
        mode: "mix",
        from: layer({ opacity: fade, scale: 1 / Math.max(0.04, zoom) }),
        to: layer({ opacity: 1 - fade }),
        ...plain,
      };
    }
    case "circle": {
      // Incoming where distance / halfDiagonal + (q − 0.5) · 3 < 0, soft over one half diagonal.
      const inner = (p - 0.5) * 3;
      return {
        mode: "radial",
        from: layer(),
        to: layer(),
        inner,
        outer: inner + 1,
        ...plain,
      };
    }
  }
}

/** Sound during a transition: the clips cross-fade linearly. */
export function transitionGains(progress: number): { from: number; to: number } {
  const p = Math.max(0, Math.min(1, progress));
  return { from: 1 - p, to: p };
}
